/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useState, useEffect } from 'react'
import classNames from 'classnames'
import Image from 'next/legacy/image'
import NextImage from 'next/image'
import logo from './logo.svg'
import s from './style.module.css'

export interface HeroProps {
	image: {
		src: string
		blurDataUrl: string
	}
	title: string
	description: string
	affiliations: Array<{
		id: string
		text: string
	}>
}

export default function Hero({
	image,
	title,
	description,
	affiliations,
}: HeroProps): JSX.Element {
	const [loaded, setLoaded] = useState(false)

	useEffect(() => {
		setTimeout(() => {
			setLoaded(true)
		}, 300)
	}, [])

	return (
		<header className={classNames(s.root, loaded && s.loaded)}>
			<div className={s.container}>
				<NextImage {...logo} alt={logo.alt || ''} />
				<div className={s.media}>
					<Image
						src={image.src}
						alt=""
						layout="fill"
						objectFit="cover"
						objectPosition="center"
						priority={true}
						placeholder="blur"
						blurDataURL={image.blurDataUrl}
					/>
					<span className={s.mediaPattern}>
						<Image
							className={s.mediaPattern}
							src="/img/industries/hero-pattern.svg"
							width={210}
							height={544}
							priority={true}
							alt=""
						/>
					</span>
				</div>
				<h1 className={s.title}>{title}</h1>
				<div
					className={s.description}
					dangerouslySetInnerHTML={{
						__html: description,
					}}
				/>
				<div className={s.card}>
					<h2 className={s.heading}>Who we work with</h2>
					<ul className={s.affiliations}>
						{affiliations.map((affliation) => (
							<li key={affliation.id}>{affliation.text}</li>
						))}
					</ul>
				</div>
			</div>
		</header>
	)
}
